import React from 'react'
import { useNavigate } from 'react-router'
import {
    Block,
    Header,
    HeaderSizes,
    ThemeContext,
    Typography,
    TypographyTypes,
    cssRules,
} from 'rds'
import { applyEffectToImage, getBackgroundImageValue } from '../../utils'
import { getIsVideo } from '../../utils/file'
import { POSITIONING } from '../../constants'
import Container from '../../layout/Container'
import Video from '../Video'

/**
 * Returns the appropriate CSS class for text alignment based on the provided alignment.
 *
 * @param {string} alignment - The alignment value which can be 'LEFT', 'RIGHT', or 'CENTER'.
 * @returns {string} - The CSS class corresponding to the provided alignment.
 */
const getPositioningClass = alignment => {
    switch (alignment) {
        case POSITIONING.LEFT:
            return 'rds-text-align_left'
        case POSITIONING.RIGHT:
            return 'rds-text-align_right'
        case POSITIONING.CENTER:
            return 'rds-text-align_center'
        default:
            return 'rds-text-align_left'
    }
}

const Hero = ({
    alignment = POSITIONING.LEFT,
    backgroundImage,
    backgroundImagePosition = 'center',
    bordered,
    effect,
    footerText,
    headerSize = HeaderSizes.BIG,
    headerVariant,
    margin,
    navigateToURL,
    overlayColor = '#000000',
    overlayColorAlpha = 0,
    padding,
    roundedBorder,
    variant,
    text,
    title,
}) => {
    const themeContext = React.useContext(ThemeContext)
    const navigate = useNavigate()
    const isVideo = getIsVideo(backgroundImage)
    const style = {
        cursor: navigateToURL ? 'pointer' : undefined,
        padding: padding
            ? `calc(${cssRules.getCSSVariable(
                  'spacingLarge',
              )} + ${padding}px) calc(${cssRules.getCSSVariable(
                  'spacingSmall',
              )} + ${padding}px)`
            : undefined,
        margin,
        height: '100%',
    }
    if (!isVideo) {
        style.backgroundImage = getBackgroundImageValue(
            applyEffectToImage(backgroundImage, effect, themeContext),
            overlayColor,
            overlayColorAlpha,
        )
        style.backgroundPosition = backgroundImagePosition
    }

    const handleClick = () => {
        navigate(navigateToURL)
    }

    return (
        <Block
            className='hero rds-p_top rds-p_bottom rds-p_left__sm rds-p_right__sm'
            roundedBorder={roundedBorder}
            bordered={bordered}
            variant={variant}
            style={style}
            onClick={navigateToURL ? handleClick : null}
        >
            {isVideo ? (
                <Video
                    url={applyEffectToImage(
                        backgroundImage,
                        effect,
                        themeContext,
                    )}
                    overlayColor={overlayColor}
                    overlayColorAlpha={overlayColorAlpha}
                    objectPosition={backgroundImagePosition}
                    loop
                />
            ) : null}
            <Container className='rds-relative'>
                <Header
                    className={`hero-header ${getPositioningClass(alignment)}`}
                    title={title}
                    text={text}
                    variant={headerVariant}
                    size={headerSize}
                />
                {footerText ? (
                    <Typography
                        className={`hero-footer rds-m_top__md ${getPositioningClass(alignment)}`}
                        type={TypographyTypes.H4}
                        variant={headerVariant}
                    >
                        {footerText}
                    </Typography>
                ) : null}
            </Container>
        </Block>
    )
}

export default Hero
